import React, { useState, useEffect } from 'react';
import {
  Modal,
  CircularProgress,
} from '@mui/material';
import { X, Users, AlertTriangle, Info } from 'lucide-react';
import { useCommercialPlan } from 'contexts';
import { ICommercialPlanViewModel, IUserViewModel } from 'viewModels';
import {
  ModalWrapper,
  ModalHeader,
  ModalTitleContainer,
  ModalTitle,
  ModalDescription,
  ModalContent,
  ModalFooter,
  CloseButton,
  FormGroup,
  FormSection,
  FormSectionTitle,
  FormLayout,
  InfoAlert,
  WarningAlert,
  ErrorAlert,
  LoadingContainer,
  LoadingText,
} from './modalStyles';

interface TransferUsersModalProps {
  open: boolean;
  onClose: () => void;
  sourcePlan: ICommercialPlanViewModel;
  onSuccess: () => void;
}

const TransferUsersModal: React.FC<TransferUsersModalProps> = ({
  open,
  onClose,
  sourcePlan,
  onSuccess,
}) => {
  const {
    handleGetPlanUsers,
    handleTransferUsers,
    commercialPlans
  } = useCommercialPlan();

  const [loading, setLoading] = useState(false);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [users, setUsers] = useState<IUserViewModel[]>([]);
  const [targetPlanId, setTargetPlanId] = useState<string>('');
  const [availablePlans, setAvailablePlans] = useState<ICommercialPlanViewModel[]>([]);

  useEffect(() => {
    if (open && sourcePlan) {
      loadUsers();
      loadAvailablePlans();
    }
  }, [open, sourcePlan]);

  const loadUsers = async () => {
    if (!sourcePlan) return;

    setLoadingUsers(true);
    try {
      const planUsers = await handleGetPlanUsers(sourcePlan.id);
      setUsers(planUsers);
    } catch (error) {
      setError('Erro ao carregar usuários do plano');
    } finally {
      setLoadingUsers(false);
    }
  };

  const loadAvailablePlans = () => {
    // Filtra planos ativos que não são o plano atual
    const available = commercialPlans.filter(
      plan => plan.isActive && plan.id !== sourcePlan.id
    );
    setAvailablePlans(available);

    // Define o primeiro plano disponível como padrão
    if (available.length > 0 && !targetPlanId) {
      setTargetPlanId(available[0].id);
    }
  };



  const handleTransfer = async () => {
    if (!targetPlanId) {
      setError('Selecione um plano de destino');
      return;
    }

    if (users.length === 0) {
      setError('Não há usuários para transferir');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // Transferir todos os usuários do plano
      const allUserIds = users.map(user => user.id);
      const success = await handleTransferUsers(sourcePlan.id, targetPlanId, allUserIds);

      if (success) {
        // Atualiza a lista de usuários
        await loadUsers();

        // Chama o callback de sucesso
        onSuccess();

        // Fecha o modal
        onClose();
      } else {
        setError('Erro ao transferir usuários');
      }

    } catch (error) {
      setError('Erro ao transferir usuários');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      setError(null);
      setTargetPlanId('');
      onClose();
    }
  };

  const targetPlan = availablePlans.find(plan => plan.id === targetPlanId);

  return (
    <Modal
      open={open}
      onClose={handleClose}
      disableScrollLock={false}
      keepMounted={false}
      aria-labelledby="transfer-modal-title"
      aria-describedby="transfer-modal-description"
    >
      <ModalWrapper style={{ maxWidth: '800px' }}>
        <ModalHeader>
          <ModalTitleContainer>
            <ModalTitle id="transfer-modal-title">
              Transferir Usuários
            </ModalTitle>
            <ModalDescription id="transfer-modal-description">
              Transfira usuários do plano "{sourcePlan?.name}" para outro plano ativo
            </ModalDescription>
          </ModalTitleContainer>
          <CloseButton onClick={handleClose}>
            <X size={20} />
          </CloseButton>
        </ModalHeader>

        <ModalContent>
          {error && (
            <ErrorAlert>
              <AlertTriangle size={16} />
              {error}
            </ErrorAlert>
          )}

          <FormLayout>
            <div>
              <FormSection>
                <FormSectionTitle>Plano de Destino</FormSectionTitle>

                <FormGroup>
                  <label style={{ fontSize: '0.875rem', color: '#64748b', marginBottom: '8px' }}>
                    Selecione o plano para onde os usuários serão transferidos:
                  </label>
                  <select
                    value={targetPlanId}
                    onChange={(e) => setTargetPlanId(e.target.value)}
                    disabled={loading}
                    style={{
                      width: '100%',
                      padding: '8px 12px',
                      border: '1px solid #d1d5db',
                      borderRadius: '4px',
                      fontSize: '0.875rem',
                      backgroundColor: 'white',
                    }}
                  >
                    <option value="">Selecione um plano...</option>
                    {availablePlans.map(plan => (
                      <option key={plan.id} value={plan.id}>
                        {plan.name} - {plan.userCount} usuário(s)
                      </option>
                    ))}
                  </select>
                </FormGroup>

                {targetPlan && (
                  <InfoAlert>
                    <Info size={16} />
                    <div>
                      <strong>Plano selecionado:</strong> {targetPlan.name}
                      <br />
                      Consumo mínimo: R$ {(targetPlan.minimumConsumption / 100).toFixed(2)}
                      <br />
                      Máx. fontes públicas: {targetPlan.maxPublicFonts}
                    </div>
                  </InfoAlert>
                )}

                {availablePlans.length === 0 && (
                  <WarningAlert>
                    <AlertTriangle size={16} />
                    Não há outros planos ativos disponíveis para transferência.
                  </WarningAlert>
                )}
              </FormSection>

              <FormSection>
                <FormSectionTitle>
                  Usuários do Plano "{sourcePlan?.name}"
                </FormSectionTitle>

                {loadingUsers ? (
                  <LoadingContainer>
                    <CircularProgress size={20} />
                    <LoadingText>Carregando usuários...</LoadingText>
                  </LoadingContainer>
                ) : users.length === 0 ? (
                  <div style={{ textAlign: 'center', padding: '20px', color: '#64748b' }}>
                    <Users size={48} style={{ marginBottom: '8px', opacity: 0.5 }} />
                    <p>Nenhum usuário encontrado neste plano</p>
                  </div>
                ) : (
                  <div style={{ maxHeight: '300px', overflowY: 'auto', border: '1px solid #e5e7eb', borderRadius: '4px' }}>
                    {users.map(user => (
                      <div
                        key={user.id}
                        style={{
                          padding: '12px',
                          borderBottom: '1px solid #f3f4f6',
                          display: 'flex',
                          alignItems: 'center',
                          backgroundColor: 'white',
                        }}
                      >
                        <div style={{ flex: 1 }}>
                          <div style={{ fontWeight: '500', color: '#1e293b' }}>
                            {user.name}
                          </div>
                          <div style={{ fontSize: '0.75rem', color: '#64748b' }}>
                            {user.email}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </FormSection>
            </div>

            <div>
              <FormSection>
                <FormSectionTitle>Resumo da Transferência</FormSectionTitle>

                <FormGroup>
                  <label style={{ fontSize: '0.875rem', color: '#64748b', marginBottom: '4px' }}>
                    Usuários que serão transferidos
                  </label>
                  <div style={{ fontSize: '1.125rem', fontWeight: '600', color: '#1e293b', marginBottom: '16px' }}>
                    {users.length} usuário{users.length !== 1 ? 's' : ''}
                  </div>
                </FormGroup>

                <FormGroup>
                  <label style={{ fontSize: '0.875rem', color: '#64748b', marginBottom: '4px' }}>
                    Plano de origem
                  </label>
                  <div style={{ fontSize: '0.875rem', color: '#1e293b', marginBottom: '16px' }}>
                    {sourcePlan?.name}
                  </div>
                </FormGroup>

                <FormGroup>
                  <label style={{ fontSize: '0.875rem', color: '#64748b', marginBottom: '4px' }}>
                    Plano de destino
                  </label>
                  <div style={{ fontSize: '0.875rem', color: '#1e293b', marginBottom: '16px' }}>
                    {targetPlan?.name || 'Não selecionado'}
                  </div>
                </FormGroup>

                <WarningAlert>
                  <AlertTriangle size={16} />
                  <div>
                    <strong>Atenção:</strong> Esta ação irá transferir TODOS os usuários
                    do plano "{sourcePlan?.name}" para o plano de destino. Os usuários manterão acesso ao sistema,
                    mas estarão sujeitos às regras e limitações do novo plano.
                  </div>
                </WarningAlert>
              </FormSection>
            </div>
          </FormLayout>
        </ModalContent>

        <ModalFooter>
          <button
            onClick={handleClose}
            disabled={loading}
            style={{
              padding: '8px 16px',
              border: '1px solid #d1d5db',
              borderRadius: '4px',
              background: 'white',
              color: '#374151',
              cursor: 'pointer',
              fontSize: '0.875rem',
              fontWeight: '500',
            }}
          >
            Cancelar
          </button>
          <button
            onClick={handleTransfer}
            disabled={loading || !targetPlanId || users.length === 0 || availablePlans.length === 0}
            style={{
              padding: '8px 16px',
              border: 'none',
              borderRadius: '4px',
              background: '#dc2626',
              color: 'white',
              cursor: 'pointer',
              fontSize: '0.875rem',
              fontWeight: '500',
              opacity: loading || !targetPlanId || users.length === 0 || availablePlans.length === 0 ? 0.5 : 1,
            }}
          >
            {loading ? (
              <LoadingContainer>
                <CircularProgress size={16} />
                <LoadingText>Transferindo...</LoadingText>
              </LoadingContainer>
            ) : (
              `Transferir ${users.length} usuário${users.length !== 1 ? 's' : ''}`
            )}
          </button>
        </ModalFooter>
      </ModalWrapper>
    </Modal>
  );
};

export default TransferUsersModal;
